import { Controller } from "@hotwired/stimulus"
import { post, patch, destroy } from "@rails/request.js"
import { escapeHtml } from "../lib/html_sanitizer"
import { EMPTY_VALUE, setValueOrEmpty, valueOrEmptyHtml } from "../lib/empty_value"
import { showConfirmModal } from "../lib/custom_confirm"
import { formatNoteTimestamp } from "../lib/format_note_timestamp"
import { positionFixedMenu } from "../lib/anchored_menu_position"
import { effectiveZoom } from "../lib/select2_manager"
import { ROW_ACTION_CLASS, ROW_ACTION_DANGER_CLASS, ROW_ACTIONS_STRIP_CLASS, fieldDeleteButtonHtml } from "../lib/row_action"
import { setStatusBadge, statusBadgeHtml } from "../lib/status_badge"
import { fieldToken, SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint"
import { syncGateReason } from "../lib/gate_reason"

// BUTTON-9: a row's Save is disabled until its label / description differ
// from what they held when Edit opened (and again once reverted); the add
// row's Save until a label is entered. One delegated input listener feeds
// both, and syncGateReason writes the reason tooltip and accessible name.
const ROW_EDIT_FIELDS = ["labelInput", "descInput"]

// Left gutter a right-anchored notes popup keeps from the viewport edge when
// its button sits closer to the left edge than the popup is wide (390px).
const NOTES_POPUP_GUTTER_PX = 8


// Connects to data-controller="database-field-configs"
export default class extends Controller {
  static targets = [
    "addButton", "addRow", "addSubmitButton",
    "newLabel", "newFieldKey", "newFieldType", "newDescription",
    "tableBody",
    "labelDisplay", "labelInput", "labelCell",
    "descDisplay", "descInput", "descCell",
    "editButton", "saveButton", "cancelButton",
    "archiveButton",
    "notesButton", "notesGroup", "notesPopup", "notesList", "noteInput", "noteSubmitButton"
  ]

  static values = { bucket: String }

  connect() {
    this.rowBaselines = new WeakMap()
    this._onFieldEdit = this.#handleFieldEdit.bind(this)
    this.element.addEventListener("input", this._onFieldEdit)
    this.#syncAddSave()
    this._onDocClick = this.#handleDocClick.bind(this)
    document.addEventListener("click", this._onDocClick, true)
    // The notes popup is position:fixed (so the card's horizontal scroller
    // cannot clip it); a scroll of the scroller or the page, or a resize,
    // would leave it floating away from its row, so it closes instead. Scroll
    // does not bubble, hence the capture-phase document listener — which also
    // sees the popup's OWN notes list scrolling (max-height + overflow-y).
    // That scroll is the reader working through a long list and moves
    // nothing the popup is anchored to, so it is ignored (#14061 root review).
    this._onOuterScroll = this.#handleOuterScroll.bind(this)
    this._onResize = this.#closeAllNotes.bind(this)
    document.addEventListener("scroll", this._onOuterScroll, { capture: true, passive: true })
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick, true)
    document.removeEventListener("scroll", this._onOuterScroll, { capture: true })
    window.removeEventListener("resize", this._onResize)
    this.element.removeEventListener("input", this._onFieldEdit)
  }

  // ── Save gating (BUTTON-9) ─────────────────────────────────────────────────

  #handleFieldEdit(event) {
    const el = event.target
    if (!(el instanceof Element)) return
    const target = el.getAttribute("data-database-field-configs-target")
    if (ROW_EDIT_FIELDS.includes(target)) {
      const row = this.#rowFor(el)
      if (row) this.#syncRowSave(row)
    } else if (target === "newLabel" || target === "newFieldKey") {
      this.#syncAddSave()
    }
  }

  #rowFingerprint(row) {
    return ROW_EDIT_FIELDS
      .map(name => row.querySelector(`[data-database-field-configs-target='${name}']`))
      .filter(Boolean)
      .map(fieldToken)
      .join("|")
  }

  #syncRowSave(row) {
    const saveBtn = row.querySelector("[data-database-field-configs-target='saveButton']")
    if (!saveBtn) return
    const ready = this.rowBaselines.has(row) && this.#rowFingerprint(row) !== this.rowBaselines.get(row)
    syncGateReason(saveBtn, { ready, reason: SAVE_UNCHANGED_REASON })
  }

  #syncAddSave() {
    if (!this.hasAddSubmitButtonTarget || !this.hasNewLabelTarget) return
    syncGateReason(this.addSubmitButtonTarget, {
      ready: this.newLabelTarget.value.trim() !== "",
      reason: SAVE_UNCHANGED_REASON
    })
  }

  // ── Add field ──────────────────────────────────────────────────────────────

  showAddForm() {
    this.fieldKeyDirty = false
    this.addRowTarget.classList.remove("d-none")
    this.addButtonTarget.classList.add("d-none")
    this.newLabelTarget.focus()
  }

  cancelAdd() {
    this.#resetAddForm()
  }

  // Auto-derive the field_key from the label until the user manually edits the field_key cell.
  deriveFieldKey() {
    if (this.fieldKeyDirty) return
    this.newFieldKeyTarget.value = this.#slugify(this.newLabelTarget.value)
  }

  // Once the user types in the field_key cell, stop overwriting it from the label.
  markFieldKeyDirty() {
    this.fieldKeyDirty = true
  }

  async submitAdd() {
    const label      = this.newLabelTarget.value.trim()
    const fieldKey   = this.newFieldKeyTarget.value.trim()
    const fieldType  = this.newFieldTypeTarget.value
    const description = this.newDescriptionTarget.value.trim()

    if (!label || !fieldKey) return

    this.addSubmitButtonTarget.disabled = true
    this.addSubmitButtonTarget.setAttribute("aria-busy", "true")

    try {
      const response = await post("/admin/system/database-field-configs.json", {
        body: JSON.stringify({
          jsonb_field_config: { bucket: this.bucketValue, field_key: fieldKey, label, field_type: fieldType, description }
        }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#appendRow(data)
        this.#resetAddForm()
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to add field", "danger")
      }
    } catch {
      window.showToast("Failed to add field", "danger")
    } finally {
      this.addSubmitButtonTarget.removeAttribute("aria-busy")
      this.#syncAddSave()
    }
  }

  // ── Inline edit ────────────────────────────────────────────────────────────

  startEdit(event) {
    const row = this.#rowFor(event.currentTarget)
    row.querySelector("[data-database-field-configs-target='labelDisplay']")?.classList.add("d-none")
    row.querySelector("[data-database-field-configs-target='labelInput']")?.classList.remove("d-none")
    row.querySelector("[data-database-field-configs-target='descDisplay']")?.classList.add("d-none")
    row.querySelector("[data-database-field-configs-target='descInput']")?.classList.remove("d-none")
    this.#setEditMode(row, true)
    this.rowBaselines.set(row, this.#rowFingerprint(row))
    this.#syncRowSave(row)
    row.querySelector("[data-database-field-configs-target='labelInput']")?.focus()
  }

  cancelEdit(event) {
    const row = this.#rowFor(event.currentTarget)
    this.#exitEdit(row)
  }

  editKeydown(event) {
    const row = this.#rowFor(event.currentTarget)
    if (event.key === "Enter") {
      event.preventDefault()
      row.querySelector("[data-database-field-configs-target='saveButton']")?.click()
    } else if (event.key === "Escape") {
      this.#exitEdit(row)
    }
  }

  async saveEdit(event) {
    const saveBtn = event.currentTarget
    const row     = this.#rowFor(saveBtn)
    const id      = row.dataset.configId
    const label   = row.querySelector("[data-database-field-configs-target='labelInput']")?.value.trim()
    const desc    = row.querySelector("[data-database-field-configs-target='descInput']")?.value.trim()

    if (!label) return

    saveBtn.disabled = true
    saveBtn.setAttribute("aria-busy", "true")

    try {
      const response = await patch(`/admin/system/database-field-configs/${id}.json`, {
        body: JSON.stringify({ jsonb_field_config: { label, description: desc } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        row.querySelector("[data-database-field-configs-target='labelDisplay']").textContent = data.label
        row.querySelector("[data-database-field-configs-target='labelInput']").value = data.label
        row.querySelector(".gl-row-actions")?.setAttribute("aria-label", `${data.label} actions`)
        const descDisplay = row.querySelector("[data-database-field-configs-target='descDisplay']")
        setValueOrEmpty(descDisplay, data.description)
        row.querySelector("[data-database-field-configs-target='descInput']").value = data.description || ""
        this.#exitEdit(row)
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to update field", "danger")
      }
    } catch {
      window.showToast("Failed to update field", "danger")
    } finally {
      saveBtn.removeAttribute("aria-busy")
      this.#syncRowSave(row)
    }
  }

  // ── Archive ────────────────────────────────────────────────────────────────

  async archive(event) {
    const row = this.#rowFor(event.currentTarget)
    const id  = row.dataset.configId
    const label = row.querySelector("[data-database-field-configs-target='labelDisplay']")?.textContent

    const confirmed = await showConfirmModal(`Archive field "${label}"? It will no longer appear as active.`)
    if (!confirmed) return

    try {
      const response = await patch(`/admin/system/database-field-configs/${id}/archive.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#applyArchivedState(row, data)
        window.showToast(`"${data.label}" archived`, "success")
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to archive field", "danger")
      }
    } catch {
      window.showToast("Failed to archive field", "danger")
    }
  }

  // ── Restore ────────────────────────────────────────────────────────────────

  async restore(event) {
    const row = this.#rowFor(event.currentTarget)
    const id  = row.dataset.configId

    try {
      const response = await patch(`/admin/system/database-field-configs/${id}/restore.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.#applyRestoredState(row, data)
        window.showToast(`"${data.label}" restored`, "success")
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to restore field", "danger")
      }
    } catch {
      window.showToast("Failed to restore field", "danger")
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  async deleteConfig(event) {
    const row   = this.#rowFor(event.currentTarget)
    const id    = row.dataset.configId
    const label = row.querySelector("[data-database-field-configs-target='labelDisplay']")?.textContent

    const confirmed = await showConfirmModal(`Permanently delete field "${label}"? This cannot be undone.`)
    if (!confirmed) return

    try {
      const response = await destroy(`/admin/system/database-field-configs/${id}.json`, {
        responseKind: "json"
      })

      if (response.ok) {
        row.remove()
        window.showToast(`"${label}" deleted`, "success")
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to delete field", "danger")
      }
    } catch {
      window.showToast("Failed to delete field", "danger")
    }
  }

  // ── Notes popup ────────────────────────────────────────────────────────────

  toggleNotes(event) {
    const row   = this.#rowFor(event.currentTarget)
    const popup = row.querySelector("[data-database-field-configs-target='notesPopup']")
    if (!popup) return

    const isOpen = !popup.classList.contains("d-none")
    // Close all other open popups first
    this.#closeAllNotes()

    if (!isOpen) {
      popup.classList.remove("d-none")
      this.#placeNotesPopup(popup, event.currentTarget)
      popup.querySelector("[data-database-field-configs-target='noteInput']")?.focus()
    }
  }

  // Shared anchored-popover placement (lib/anchored_menu_position): fixed,
  // right edge on the button's right edge, flipped above when the viewport
  // bottom is near, height-clamped when neither side fits. Then keep the left
  // edge on screen — at 390px a 320px popup anchored to a button near the
  // left edge would otherwise start off-canvas.
  #placeNotesPopup(popup, button) {
    positionFixedMenu(popup, button.getBoundingClientRect(), { align: "right" })
    // The helper writes viewport px. Inside the desktop body's `zoom: 0.9`
    // (_layout.scss) a fixed element's offsets are read in pre-zoom units, so
    // they must be divided by the effective zoom — the same compensation the
    // React hover cards apply (frontend/react/lib/zoom.ts). Mobile is unzoomed.
    const zoom = effectiveZoom(popup)
    if (zoom !== 1) {
      for (const prop of ["top", "bottom", "left", "right", "maxHeight"]) {
        const value = popup.style[prop]
        if (value.endsWith("px")) popup.style[prop] = `${parseFloat(value) / zoom}px`
      }
    }
    if (popup.getBoundingClientRect().left < NOTES_POPUP_GUTTER_PX) {
      popup.style.right = "auto"
      popup.style.left = `${NOTES_POPUP_GUTTER_PX / zoom}px`
    }
  }

  #closeAllNotes() {
    this.notesPopupTargets.forEach(p => p.classList.add("d-none"))
  }

  // A scroll whose target sits inside an OPEN popup is that popup's own list
  // scrolling; everything else (the card scroller, the document) moves the
  // anchor away from the fixed popup and closes it.
  #handleOuterScroll(event) {
    const target = event.target instanceof Node ? event.target : null
    const insideOpenPopup = target && this.notesPopupTargets.some(
      popup => !popup.classList.contains("d-none") && popup.contains(target)
    )
    if (insideOpenPopup) return
    this.#closeAllNotes()
  }

  noteKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.#rowFor(event.currentTarget)
        ?.querySelector("[data-database-field-configs-target='noteSubmitButton']")
        ?.click()
    }
  }

  async addNote(event) {
    const row      = this.#rowFor(event.currentTarget)
    const id       = row.dataset.configId
    const input    = row.querySelector("[data-database-field-configs-target='noteInput']")
    const submitBtn = row.querySelector("[data-database-field-configs-target='noteSubmitButton']")
    const body     = input?.value.trim()

    if (!body) return

    submitBtn.disabled = true

    try {
      const response = await post(`/admin/system/database-field-configs/${id}/database_field_notes.json`, {
        body: JSON.stringify({ jsonb_field_note: { body } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const note = await response.json
        const notesList = row.querySelector("[data-database-field-configs-target='notesList']")
        const emptyMsg = notesList?.querySelector(".text-center.text-body-secondary")
        if (emptyMsg) emptyMsg.remove()
        notesList?.insertAdjacentHTML("afterbegin", this.#buildNoteHtml(note))
        if (input) input.value = ""
      } else {
        const error = await response.json
        window.showToast(error.error || "Failed to add note", "danger")
      }
    } catch {
      window.showToast("Failed to add note", "danger")
    } finally {
      submitBtn.disabled = false
    }
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  #handleDocClick(event) {
    this.notesPopupTargets.forEach(popup => {
      if (popup.classList.contains("d-none")) return
      const wrapper = popup.closest("[data-database-field-configs-target='notesGroup']")
      if (!wrapper || !wrapper.contains(event.target)) {
        popup.classList.add("d-none")
      }
    })
  }

  #rowFor(element) {
    return element.closest("tr")
  }

  #setEditMode(row, editing) {
    const editBtn   = row.querySelector("[data-database-field-configs-target='editButton']")
    const saveBtn   = row.querySelector("[data-database-field-configs-target='saveButton']")
    const cancelBtn = row.querySelector("[data-database-field-configs-target='cancelButton']")
    const archiveBtn = row.querySelector("[data-database-field-configs-target='archiveButton']")

    editBtn?.classList.toggle("d-none", editing)
    saveBtn?.classList.toggle("d-none", !editing)
    cancelBtn?.classList.toggle("d-none", !editing)
    archiveBtn?.classList.toggle("d-none", editing)
  }

  #exitEdit(row) {
    // Put the inputs back to the shown (saved) values, as custom_fields does:
    // a cancelled draft left in the input would become the next edit's
    // baseline and read clean while it differs from what is saved (BUTTON-9).
    const labelDisplay = row.querySelector("[data-database-field-configs-target='labelDisplay']")
    const labelInput = row.querySelector("[data-database-field-configs-target='labelInput']")
    const descDisplay = row.querySelector("[data-database-field-configs-target='descDisplay']")
    const descInput = row.querySelector("[data-database-field-configs-target='descInput']")
    if (labelInput && labelDisplay) labelInput.value = labelDisplay.textContent.trim()
    if (descInput && descDisplay) {
      const text = descDisplay.textContent.trim()
      descInput.value = text === EMPTY_VALUE ? "" : text
    }
    row.querySelector("[data-database-field-configs-target='labelDisplay']")?.classList.remove("d-none")
    row.querySelector("[data-database-field-configs-target='labelInput']")?.classList.add("d-none")
    row.querySelector("[data-database-field-configs-target='descDisplay']")?.classList.remove("d-none")
    row.querySelector("[data-database-field-configs-target='descInput']")?.classList.add("d-none")
    this.#setEditMode(row, false)
  }

  #resetAddForm() {
    this.newLabelTarget.value = ""
    this.newFieldKeyTarget.value = ""
    this.newFieldTypeTarget.value = "datetime"
    this.newDescriptionTarget.value = ""
    this.fieldKeyDirty = false
    this.addRowTarget.classList.add("d-none")
    this.addButtonTarget.classList.remove("d-none")
    this.#syncAddSave()
  }

  // Mirrors the Ruby validation regex [a-z][a-z0-9_]*: lowercase, replace runs
  // of non-alphanumerics with a single underscore, strip leading/trailing
  // underscores. Labels that start with a digit get prefixed with "f_" so the
  // derived field_key still matches the leading-lowercase-letter rule.
  #slugify(label) {
    const key = label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
    if (!key) return ""
    return /^[0-9]/.test(key) ? `f_${key}` : key
  }

  #applyArchivedState(row, data) {
    row.dataset.archived = "true"
    row.classList.add("gl-row-archived")
    const statusCell = row.querySelector(".gl-status-badge")
    if (statusCell) setStatusBadge(statusCell, "Archived", "secondary")
    const actionsDiv = this.#rowActionsStrip(row)
    if (!actionsDiv) return
    // Remove archive + any existing delete buttons, insert restore + possibly delete before Notes
    row.querySelectorAll("[data-action='click->database-field-configs#deleteConfig']").forEach(b => b.remove())
    const archiveBtn = row.querySelector("[data-database-field-configs-target='archiveButton']")
    archiveBtn?.remove()
    const notesWrapper = actionsDiv.querySelector("[data-database-field-configs-target='notesGroup']")
    const html = `
      <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Restore" aria-label="Restore"
              data-action="click->database-field-configs#restore">
        <i class="ph ph-arrow-counter-clockwise"></i>
      </button>
      ${fieldDeleteButtonHtml("click->database-field-configs#deleteConfig", data.has_data)}
    `
    notesWrapper
      ? notesWrapper.insertAdjacentHTML("beforebegin", html)
      : actionsDiv.insertAdjacentHTML("beforeend", html)
  }

  #applyRestoredState(row, data) {
    row.dataset.archived = "false"
    row.classList.remove("gl-row-archived")
    const statusCell = row.querySelector(".gl-status-badge")
    if (statusCell) setStatusBadge(statusCell, "Active", "success")
    const actionsDiv = this.#rowActionsStrip(row)
    if (!actionsDiv) return
    // Remove restore + delete buttons, re-add archive + possibly delete before Notes
    row.querySelectorAll("[data-action='click->database-field-configs#restore']").forEach(b => b.remove())
    row.querySelectorAll("[data-action='click->database-field-configs#deleteConfig']").forEach(b => b.remove())
    const notesWrapper = actionsDiv.querySelector("[data-database-field-configs-target='notesGroup']")
    const html = `
      <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Archive" aria-label="Archive"
              data-action="click->database-field-configs#archive"
              data-database-field-configs-target="archiveButton">
        <i class="ph ph-archive"></i>
      </button>
      ${fieldDeleteButtonHtml("click->database-field-configs#deleteConfig", data.has_data)}
    `
    notesWrapper
      ? notesWrapper.insertAdjacentHTML("beforebegin", html)
      : actionsDiv.insertAdjacentHTML("beforeend", html)
  }

  // The row's action run (gl-row-actions). When it is missing, the caller leaves the
  // row's existing buttons in place (never strips them with nothing to put
  // back) and the user is told to reload.
  #rowActionsStrip(row) {
    const strip = row.querySelector(".gl-row-actions")
    if (!strip) window.showToast("Saved. Reload the page to see this field's updated actions.", "warning")
    return strip
  }

  #appendRow(data) {
    const typeClass = `gl-field-type-badge--${escapeHtml(data.field_type)}`
    const html = `
      <tr id="field-config-${data.id}"
          data-config-id="${data.id}"
          data-has-data="${data.has_data}"
          data-archived="false">
        <td class="ps-3">
          <strong data-database-field-configs-target="labelDisplay">${escapeHtml(data.label)}</strong>
          <input type="text" class="gl-form-control form-control-sm d-none" value="${escapeHtml(data.label)}"
                 maxlength="100"
                 data-database-field-configs-target="labelInput"
                 data-action="keydown->database-field-configs#editKeydown">
        </td>
        <td><span class="gl-field-key">${escapeHtml(data.field_key)}</span></td>
        <td><span class="gl-field-type-badge ${typeClass}">${escapeHtml(data.field_type)}</span></td>
        <td>
          <span class="text-body-secondary" data-database-field-configs-target="descDisplay">${valueOrEmptyHtml(data.description)}</span>
          <input type="text" class="gl-form-control form-control-sm d-none" value="${escapeHtml(data.description || "")}"
                 maxlength="500"
                 data-database-field-configs-target="descInput"
                 data-action="keydown->database-field-configs#editKeydown">
        </td>
        <td>${statusBadgeHtml("Active", "success")}</td>
        <td class="pe-3">
          <div class="gl-table-actions">
          <div class="${ROW_ACTIONS_STRIP_CLASS}" role="group" aria-label="${escapeHtml(data.label)} actions">
            <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Edit" aria-label="Edit"
                    data-action="click->database-field-configs#startEdit"
                    data-database-field-configs-target="editButton">
              <i class="ph ph-pencil-simple"></i>
            </button>
            <button class="${ROW_ACTION_CLASS} d-none" type="button" data-gl-tooltip="Save" aria-label="Save"
                    data-action="click->database-field-configs#saveEdit"
                    data-database-field-configs-target="saveButton">
              <i class="ph ph-check"></i>
            </button>
            <button class="${ROW_ACTION_CLASS} d-none" type="button" data-gl-tooltip="Cancel" aria-label="Cancel"
                    data-action="click->database-field-configs#cancelEdit"
                    data-database-field-configs-target="cancelButton">
              <i class="ph ph-x"></i>
            </button>
            <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Archive" aria-label="Archive"
                    data-action="click->database-field-configs#archive"
                    data-database-field-configs-target="archiveButton">
              <i class="ph ph-archive"></i>
            </button>
            <button class="${ROW_ACTION_DANGER_CLASS}" type="button" data-gl-tooltip="Delete" aria-label="Delete"
                    data-action="click->database-field-configs#deleteConfig">
              <i class="ph ph-trash"></i>
            </button>
            <div data-database-field-configs-target="notesGroup">
              <button class="${ROW_ACTION_CLASS}" type="button" data-gl-tooltip="Field notes" aria-label="Field notes"
                      data-action="click->database-field-configs#toggleNotes"
                      data-database-field-configs-target="notesButton">
                <i class="ph ph-chat-text"></i>
              </button>
              <div class="gl-field-notes-popup d-none" data-database-field-configs-target="notesPopup">
                <div class="gl-field-notes-popup__header">
                  <span><i class="ph ph-chat-text me-1"></i>Field Notes</span>
                  <small class="ms-auto gl-field-key me-2">${escapeHtml(data.field_key)}</small>
                </div>
                <div class="gl-field-notes-popup__body" data-database-field-configs-target="notesList">
                  <div class="text-center text-body-secondary py-3 small">No notes yet.</div>
                </div>
                <div class="gl-field-notes-popup__footer">
                  <input class="gl-form-control form-control-sm" type="text" placeholder="Add a note…"
                         data-database-field-configs-target="noteInput"
                         data-action="keydown->database-field-configs#noteKeydown">
                  <button class="gl-btn gl-btn-primary gl-btn-sm" type="button"
                          data-action="click->database-field-configs#addNote"
                          data-database-field-configs-target="noteSubmitButton">
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
          </div>
        </td>
      </tr>
    `
    this.tableBodyTarget.insertAdjacentHTML("beforeend", html)
  }

  #buildNoteHtml(note) {
    const timestamp = formatNoteTimestamp(note.created_at, { withSeconds: true })
    const tag = note.note_type === "system" ? (note.action || "system") : "note"
    const tagClass = note.note_type === "system" ? "gl-field-note-badge--system" : "gl-field-note-badge--manual"
    const author = note.user_name ? ` · ${escapeHtml(note.user_name)}` : ""

    return `
      <div class="gl-field-note-item">
        <div class="gl-field-note-item__text">
          <span class="gl-field-note-badge ${tagClass}">${escapeHtml(tag)}</span>
          ${escapeHtml(note.body)}
        </div>
        <div class="gl-field-note-item__meta">${escapeHtml(timestamp)}${author}</div>
      </div>
    `
  }
}
